import React, { useState, useEffect } from 'react';
import {
  Alert, Button, Drawer, DrawerHeader, Typography
} from '@one-thd/sui-atomic-components';
import { Store, Locker } from '@one-thd/sui-icons';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { getLockerCode } from '../../api/oms/order-details';
import { ALERT_MESSAGES } from '../../constants/messages';

interface IPickupLockerQRCode {
  buttonVariant?: "primary" | "secondary" | "tertiary" | "text" | "ghost";
  orderNumber: string;
  storeNumber: string;
  customerType: string;
}

export const PickupLockerQRCode = ({
  buttonVariant,
  orderNumber,
  storeNumber,
  customerType,
}: IPickupLockerQRCode) => {
  const [open, setOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [lockerCode, setLockerCode] = useState('');

  const { svocID } = useThdCustomer();

  const thdCustomerCookie = ('; ' + document?.cookie)
    ?.split('; THD_CUSTOMER=')
    ?.pop()
    ?.split(';')?.[0];

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleModalClick = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  /**
   * `orderNumber` and `storeNumber` come from the fetched order, so they arrive
   * after the first render and change again whenever the drawer is pointed at
   * another order. The fetch therefore re-runs on its inputs rather than on
   * mount, skips a request it has no store or order to make, and discards a
   * response that lands after its inputs have already moved on — otherwise one
   * order's access code and QR image can end up on screen for another. Any
   * previously rendered code or error is cleared before each attempt, so a swap
   * between orders cannot leave the previous order's access code on screen
   * while the new request is still in flight.
   *
   * A resolved response carrying no usable access code is an error state, not a
   * success with an empty code: the QR image URL and the code readout both need
   * a real code, and the panel picks its height from `hasError` while the alert
   * keys off the empty code — left as a success, the two would disagree about
   * what the customer is looking at.
   */
  useEffect(() => {
    setHasError(false);
    setLockerCode('');

    if (!orderNumber || !storeNumber) {
      return undefined;
    }

    let current = true;

    const lockerPickupEligible = async () => {
      try {
        const request = {
          lockerCodeRequest: {
            customerAccountId: svocID ?? '',
            orderNumber,
            storeNumber,
          },
        };

        const response = await getLockerCode(
          request,
          `THD_CUSTOMER=${thdCustomerCookie}`,
        );

        if (!current) return;

        const accessCode = response?.data?.lockerAccessCodes?.[0]?.accessCode?.trim();

        setHasError(!accessCode);
        setLockerCode(accessCode || '');
      } catch (error) {
        if (!current) return;
        setHasError(true);
        setLockerCode('');
      }
    };

    lockerPickupEligible();

    return () => {
      current = false;
    };
  }, [orderNumber, storeNumber, svocID, thdCustomerCookie]);

  return (
    <div>
      {/*
        SUI renders the trigger as a Button or an anchor depending on variant.
        Both need to fill the row and the anchor needs its label centred and
        unadorned, so the overrides are scoped to this wrapper's descendants
        rather than injected as a stylesheet on every render.
      */}
      <div className="[&_a]:!sui-w-full [&_button]:!sui-w-full [&_a]:!sui-justify-center [&_a:hover]:!sui-no-underline">
        <Button
          variant={buttonVariant}
          onClick={onClick}
          data-testid="pickup-locker-qr-code-button"
        >
          View Locker Pickup QR Code
        </Button>
      </div>

      <Drawer open={open} onClose={() => handleClose()}>
        <div
          onClick={handleModalClick}
          role="button"
          tabIndex={0}
          onKeyDown={(ev: React.KeyboardEvent<HTMLDivElement>) => {
            if (ev.key === 'Enter' || ev.key === ' ') {
              handleModalClick(ev);
            }
          }}
          style={{ height: hasError ? '345px' : '485px', cursor: 'default' }}
        >
          <DrawerHeader onClose={handleClose} title="Pickup Locker QR Code" />
          <div>
            <div className="sui-flex sui-flex-col sui-m-auto">
              {hasError || lockerCode === '' ? (
                <div className="sui-mx-4 sui-mb-3">
                  <Alert status="error" size="large">
                    <Typography variant="body-sm">
                      {ALERT_MESSAGES.PICKUP_LOCKER_QR_CODE_ERROR}
                    </Typography>
                  </Alert>
                </div>
              ) : (
                <div className="sui-flex sui-flex-col sui-items-center sui-m-auto sui-mb-1">
                  <div className="sui-overflow-hidden">
                    <img
                      src={`https://storage.googleapis.com/hd-custcomm-prod-qr-static/${lockerCode}.png`}
                      alt="QR Code Pic"
                      style={{ transform: 'scale(1.35)' }}
                      width="230"
                      height="230"
                    />
                  </div>

                  <div className="sui-flex sui-gap-1 sui-mt-1">
                    <Typography variant="body-sm" color="subtle" weight="bold">
                      Locker Code:
                    </Typography>
                    <Typography
                      variant="body-sm"
                      color="subtle"
                      weight="bold"
                      style={{ letterSpacing: '4px' }}
                    >
                      {lockerCode}
                    </Typography>
                  </div>
                </div>
              )}
              <div className="sui-flex sui-gap-2 sui-mb-2 sui-mx-4">
                <Store size="large" />
                <Typography variant="body-sm">
                  Go to your selected Home Depot store pickup location
                </Typography>
              </div>
              <div className="sui-flex sui-gap-2 sui-mx-4">
                <Locker size="large" />
                <Typography variant="body-sm">
                  Scan the QR code at the pickup lockers to retrieve your item(s).
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </div>
  );
};
